import { C as Controller, c as createEvent, t as trackKey, a as createEventFactory, p as propertyTrackResolve } from "../Controller-Cer_6Z29.js";
import { G, b, d } from "../Controller-Cer_6Z29.js";
import { a as setAmbientComponent, i as isPromise, b as setParentController, d as retrieveParentControllers, r as retrieveComponent } from "../ControllerInternals-CeDntN3G.js";
import { e, c, g, f } from "../ControllerInternals-CeDntN3G.js";
import { p as proxyExports } from "../proxyExports-BkN6hND0.js";
import { isServer } from "lit";
import { isEsriInternalEnv } from "@arcgis/toolkit/error";
import { observeAncestorsMutation, getElementAttribute } from "@arcgis/toolkit/dom";
import { getElementLocale, startLocaleObserver } from "@arcgis/toolkit/intl";
import { s as setAutoDestroyOnDisconnectTimeout, b as autoDestroyOnDisconnectTimeout } from "../ControllerManager-DpJfvft9.js";
const makeController = (constructor) => proxy(void 0, constructor);
const makeGenericController = (constructor) => (component) => proxy(
  component,
  /**
   * GenericController is identical to Controller, in all except for typing.
   * So doing a type-cast here so as not to needlessly add one more object
   * to the prototype chain
   */
  constructor
);
class FunctionalController extends Controller {
  constructor(component, constructor) {
    super(component);
    const originalExports = this.exports;
    try {
      setAmbientComponent(this.component);
      const value = constructor(this.component, this);
      const constructorChangedExports = this.exports !== originalExports;
      if (isPromise(value)) {
        if (!constructorChangedExports) {
          this.setProvisionalExports(value);
        }
        const resolved = value.then((result) => {
          this.exports = result;
          super.catchUpLifecycle();
        }).catch((error) => {
          this.P.reject(error);
        });
        this.onLoad(async () => await resolved);
      } else {
        if (!constructorChangedExports || value !== void 0) {
          this.exports = value;
        }
        queueMicrotask(() => super.catchUpLifecycle());
      }
    } catch (error) {
      this.P.reject(error);
    }
  }
  /** Noop - will be called in the constructor instead */
  catchUpLifecycle() {
    return;
  }
}
const proxy = proxyExports(FunctionalController);
const useWatchAttributes = (attributes, callback) => new AttributeWatchController(attributes, callback);
class AttributeWatchController extends Controller {
  #observer;
  #attributes;
  #callback;
  constructor(attributes, callback) {
    super();
    if (isServer) {
      return;
    }
    this.#attributes = attributes;
    this.#callback = callback;
    this.#observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (attributes.includes(mutation.attributeName)) {
          callback.call(
            this.component,
            this.component.el.getAttribute(mutation.attributeName),
            mutation.oldValue,
            mutation.attributeName
          );
        }
      });
    });
  }
  hostConnected() {
    this.#attributes.forEach((attribute) => {
      if (this.component.el.hasAttribute(attribute)) {
        this.#callback.call(this.component, this.component.el.getAttribute(attribute), null, attribute);
      }
    });
    this.#observer.observe(this.component.el, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: this.#attributes
    });
  }
  hostDisconnected() {
    this.#observer.disconnect();
  }
}
const load = makeController;
const toFunction = (Class) => (...args) => {
  const ambientControllers = retrieveParentControllers();
  const instance = new Class(...args);
  setParentController(ambientControllers.at(-1));
  return instance;
};
const useSlottableRequest = (component, watch, getDetailsBySlotName) => makeController((_component, controller) => {
  const watchedProps = watch;
  const slottableRequestController = createEvent({ bubbles: false, composed: false });
  controller.onUpdated((changes) => {
    if (!watchedProps.some((prop) => changes.has(prop))) {
      return;
    }
    const previousDetails = getDetailsBySlotName(getWatchedProps(component, watchedProps, changes));
    const currentDetails = getDetailsBySlotName(getWatchedProps(component, watchedProps));
    previousDetails.forEach((previousDetail, slotName) => {
      if (!currentDetails.has(slotName)) {
        slottableRequestController.emit({ ...previousDetail, data: void 0 });
      }
    });
    currentDetails.forEach((currentDetail, slotName) => {
      const previousDetail = previousDetails.get(slotName);
      if (!previousDetail || !isEqualValue(previousDetail, currentDetail)) {
        slottableRequestController.emit(currentDetail);
      }
    });
  });
  return slottableRequestController;
});
function getWatchedProps(component, watchedProps, changes) {
  const values = {};
  watchedProps.forEach((prop) => {
    values[prop] = changes?.has(prop) ? changes.get(prop) : component[prop];
  });
  return values;
}
function isEqualValue(previous, current) {
  if (previous === current) {
    return true;
  }
  if (!previous || !current || typeof previous !== "object" || typeof current !== "object") {
    return false;
  }
  if (Array.isArray(previous) || Array.isArray(current)) {
    return false;
  }
  const previousEntries = Object.entries(previous);
  const currentEntries = Object.entries(current);
  if (previousEntries.length !== currentEntries.length) {
    return false;
  }
  return previousEntries.every(([key, value]) => isEqualValue(value, current[key]));
}
const useMedia = (query) => makeController((_component, controller) => {
  const media = globalThis.matchMedia(query);
  const changed = () => {
    controller.exports = media.matches;
  };
  changed();
  controller.onLifecycle(() => {
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  });
  return void 0;
});
const defaultDirection = "ltr";
const useDirection = () => makeController((component, controller) => {
  controller.exports = defaultDirection;
  controller.onLifecycle(() => {
    const callback = () => {
      const dir = getElementAttribute(component.el, "dir", defaultDirection);
      controller.exports = dir === "rtl" ? "rtl" : "ltr";
    };
    callback();
    return observeAncestorsMutation(component.el, ["dir"], callback);
  });
  if (process.env.NODE_ENV !== "production" && isEsriInternalEnv()) {
    trackKey(
      component,
      (resolved) => {
        if (resolved?.key === "dir") {
          throw new Error(
            "Do not assign this controller to a prop called `dir` as that will overwrite the HTMLElement's built-in `dir` attribute - component should not be setting the `dir` attribute on itself. Instead, assign this controller to a property called `direction`."
          );
        }
      },
      controller.exports
    );
  }
  return void 0;
});
const makeT9nController = (getAssetPath) => (options = {}) => (
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  makeController((component, controller) => {
    const locale = getElementLocale(component.el);
    const pending = { ["_lang"]: locale.lang, ["_t9nLocale"]: locale.t9nLocale, ["_loading"]: true };
    const componentWithOverrides = component;
    controller.onLifecycle(
      () => startLocaleObserver(
        component.el,
        () => getAssetPath("./assets"),
        ({ t9nLocale, t9nStrings, lang }) => {
          const withoutOverrides = {
            ...t9nStrings,
            ["_lang"]: lang,
            ["_t9nLocale"]: t9nLocale,
            ["_loading"]: false
          };
          controller.exports = withoutOverrides;
          const label = t9nStrings.componentLabel;
          if (typeof label === "string" && "label" in component && component.label == null) {
            component.label ??= label;
          }
          applyOverrides(componentWithOverrides.messageOverrides);
        },
        options.name
      )
    );
    const applyOverrides = (messageOverrides) => {
      const currentValue = controller.exports;
      const rawMessages = currentValue["_original"] ?? currentValue;
      const updated = deepMerge(rawMessages, messageOverrides);
      if (messageOverrides) {
        updated["_original"] = rawMessages;
      }
      controller.exports = updated;
    };
    if ("messageOverrides" in componentWithOverrides) {
      controller.onUpdate((changes) => {
        if (changes.has("messageOverrides")) {
          applyOverrides(componentWithOverrides.messageOverrides);
        }
      });
    }
    if (options.blocking) {
      controller.setProvisionalExports(pending, false);
      return controller.ready;
    } else {
      return pending;
    }
  })
);
const deepMerge = (original, overwrites) => {
  if (!overwrites) {
    return original;
  }
  const merged = { ...original };
  Object.entries(overwrites).forEach(([key, value]) => {
    if (original[key] !== void 0) {
      if (typeof value === "object") {
        merged[key] = deepMerge(original[key], value);
      } else {
        merged[key] = value ?? original[key];
      }
    }
  });
  return merged;
};
const usePropertyChange = (_component) => propertyChangeController;
const eventName = "arcgisPropertyChange";
const propertyChangeController = (...toWatch) => {
  const component = retrieveComponent();
  const eventEmitter = createEventFactory(eventName, void 0, component);
  if (process.env.NODE_ENV !== "production" && isEsriInternalEnv()) {
    toWatch.forEach((prop) => {
      const descriptor = component.constructor.elementProperties.get(prop);
      const isProp = descriptor !== void 0 && descriptor.state !== true;
      if (!isProp) {
        throw new Error(
          `For usePropertyChange to emit event on "${prop}" property change, you should add @property() to ${prop} in ${component.el.localName}`
        );
      }
    });
  }
  let isFirst = true;
  component.manager.onUpdated((changes) => {
    if (isFirst || !component.el.isConnected) {
      isFirst = false;
      return;
    }
    for (const name of toWatch) {
      if (changes.has(name)) {
        eventEmitter.emit({ name });
      }
    }
  });
  if (process.env.NODE_ENV !== "production" && isEsriInternalEnv()) {
    trackKey(
      component,
      (resolution) => {
        if (resolution === void 0 || resolution.isReactive || resolution.key !== eventName) {
          throw new Error(
            `For consistency, usePropertyChange must be assigned to an arcgisPropertyChange property and that property should not have @property() or @state() decorators: arcgisPropertyChange = usePropertyChange<this>()("prop1", "prop2");`
          );
        }
      },
      eventEmitter
    );
  }
  return eventEmitter;
};
const keyTrackResolve = propertyTrackResolve;
const exportsForTests = {
  setAmbientComponent,
  autoDestroyOnDisconnectTimeout,
  setAutoDestroyOnDisconnectTimeout
};
export {
  Controller,
  G as GenericController,
  e as bypassReadOnly,
  c as controllerSymbol,
  exportsForTests,
  g as getControllersCount,
  f as isController,
  keyTrackResolve,
  load,
  makeController,
  makeGenericController,
  makeT9nController,
  propertyTrackResolve,
  proxyExports,
  retrieveComponent,
  toFunction,
  trackKey,
  b as trackPropKey,
  d as trackPropertyKey,
  useDirection,
  useMedia,
  usePropertyChange,
  useSlottableRequest,
  useWatchAttributes
};
